import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Fade, Zoom } from '@mui/material';
import { keyframes } from '@mui/system';

// Particle floating animation
const float = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  25% {
    transform: translateY(-20px) rotate(90deg);
  }
  50% {
    transform: translateY(-10px) rotate(180deg);
  }
  75% {
    transform: translateY(-30px) rotate(270deg);
  }
`;

// Morphing blob animation
const morph = keyframes`
  0%, 100% {
    border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
    transform: scale(1) rotate(0deg);
  }
  25% {
    border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
    transform: scale(1.1) rotate(90deg);
  }
  50% {
    border-radius: 80% 20% 40% 60% / 40% 70% 60% 30%;
    transform: scale(0.9) rotate(180deg);
  }
  75% {
    border-radius: 40% 80% 60% 30% / 70% 40% 50% 60%;
    transform: scale(1.05) rotate(270deg);
  }
`;

// Glowing pulse animation
const glow = keyframes`
  0%, 100% {
    filter: brightness(1) blur(0px);
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.3);
  }
  50% {
    filter: brightness(1.2) blur(1px);
    box-shadow: 0 0 40px rgba(102, 126, 234, 0.6), 0 0 60px rgba(118, 75, 162, 0.4);
  }
`;

// Loading text animation
const loadingText = keyframes`
  0%, 80%, 100% {
    opacity: 0;
    transform: translateY(10px);
  }
  40% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StatsLoadingOverlay = ({ 
  isLoading = true, 
  title = "Loading Stats...",
  subtitle = "Preparing your data",
  showParticles = true,
  variant = 'default' // 'default', 'minimal', 'advanced'
}) => {
  if (!isLoading) return null;

  const renderParticles = () => {
    if (!showParticles) return null;
    
    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        {[...Array(12)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: Math.random() * 8 + 4,
              height: Math.random() * 8 + 4,
              background: `linear-gradient(45deg, 
                rgba(102, 126, 234, ${Math.random() * 0.6 + 0.4}), 
                rgba(118, 75, 162, ${Math.random() * 0.6 + 0.4})
              )`,
              borderRadius: '50%',
              left: `${Math.random() * 90 + 5}%`,
              top: `${Math.random() * 90 + 5}%`,
              animation: `${float} ${Math.random() * 3 + 2}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
              filter: 'blur(0.5px)',
            }}
          />
        ))}
      </Box>
    );
  };

  const renderContent = () => {
    switch (variant) {
      case 'minimal':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                margin: '0 auto 16px',
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                borderRadius: '50%',
                animation: `${glow} 2s ease-in-out infinite`,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                animation: `${loadingText} 2s ease-in-out infinite`,
              }}
            >
              {title}
            </Typography>
          </Box>
        );

      case 'advanced':
        return (
          <Box sx={{ textAlign: 'center', position: 'relative' }}>
            {/* Morphing blob background */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 200,
                height: 200,
                background: `linear-gradient(45deg, 
                  rgba(102, 126, 234, 0.1), 
                  rgba(118, 75, 162, 0.1)
                )`,
                animation: `${morph} 8s ease-in-out infinite`,
                filter: 'blur(20px)',
                zIndex: -1,
              }}
            />
            
            {/* Central loading indicator */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <Box sx={{ position: 'relative' }}>
                {/* Outer ring */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    border: '3px solid rgba(102, 126, 234, 0.2)',
                    borderTop: '3px solid #667eea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
                {/* Inner pulse */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 40,
                    height: 40,
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    borderRadius: '50%',
                    animation: `${glow} 2s ease-in-out infinite`,
                  }}
                />
              </Box>
              
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: `${loadingText} 2s ease-in-out infinite`,
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mt: 1,
                    animation: `${loadingText} 2s ease-in-out infinite 0.5s`,
                  }}
                >
                  {subtitle}
                </Typography>
              </Box>
            </Box>
          </Box>
        );

      default:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                display: 'inline-flex',
                gap: 1,
                mb: 2,
              }}
            >
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    animation: `${glow} 1.4s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                  }}
                />
              ))}
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: 'text.primary',
                animation: `${loadingText} 2s ease-in-out infinite`,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.5,
                animation: `${loadingText} 2s ease-in-out infinite 0.3s`,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Fade in={isLoading} timeout={300}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          zIndex: 1000,
        }}
      >
        <Zoom in={isLoading} timeout={500}>
          <Box sx={{ position: 'relative' }}>
            {renderParticles()}
            {renderContent()}
          </Box>
        </Zoom>
      </Box>
    </Fade>
  );
};

StatsLoadingOverlay.propTypes = {
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  showParticles: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'minimal', 'advanced']),
};

StatsLoadingOverlay.defaultProps = {
  isLoading: true,
  title: "Loading Stats...",
  subtitle: "Preparing your data",
  showParticles: true,
  variant: 'default',
};

export default StatsLoadingOverlay;
